import { useQuery } from "@tanstack/react-query";
import { TriangleAlert } from "lucide-react";
import { useState } from "react";

import { useDataSource } from "@/adapters/data-source";
import {
  projectMissingHsnVariant,
  type MissingHsnRow,
} from "@/modules/catalog/projections";
import { LoadFailed, NoMatches } from "@/components/shell/EmptyState";
import { TableSkeleton } from "@/components/shell/LoadingState";
import { DataTable } from "@/components/table/DataTable";
import { Pagination } from "@/components/table/Pagination";
import { useCursorHistory } from "@/components/table/useCursorHistory";
import type { SectionScreenProps } from "@/modules/types";

/**
 * Variants with no HSN code (FR-CAT-11).
 *
 * A banner explains the list rather than leaving it to speak for itself —
 * without one this reads as "these products are broken" instead of what it
 * actually is: a worklist of the specific rows GST-M8 cannot invoice until
 * someone sets a code.
 *
 * `listVariantsMissingHsn` returns the variant alone; the product title it
 * needs to be recognised by lives on a separate resource, so this screen
 * resolves the small number of distinct products a page of missing
 * variants can belong to and merges the title in before projecting.
 */
export function MissingHsn({
  fields,
  filter,
  sort,
  onToggleSort,
  onClearFilter,
}: SectionScreenProps) {
  const data = useDataSource();

  const [limit, setLimit] = useState(25);
  const [history, setHistory] = useCursorHistory();
  const cursor = history[history.length - 1];

  const variants = useQuery({
    queryKey: [
      "catalog",
      "variants",
      "missing-hsn",
      filter,
      sort,
      limit,
      cursor,
    ],
    queryFn: () =>
      data.catalog.listVariantsMissingHsn({ filter, sort, limit, cursor }),
  });

  const productIds = [
    ...new Set(variants.data?.items.map((variant) => variant.productId) ?? []),
  ];

  const products = useQuery({
    queryKey: ["catalog", "products", "for-missing-hsn", productIds],
    queryFn: () =>
      Promise.all(productIds.map((id) => data.catalog.getProduct(id))),
    enabled: variants.isSuccess,
  });

  if (variants.isPending) return <TableSkeleton />;

  if (variants.isError) {
    return (
      <LoadFailed
        message={variants.error.message}
        onRetry={() => {
          void variants.refetch();
        }}
      />
    );
  }

  if (products.isPending) return <TableSkeleton />;

  if (products.isError) {
    return (
      <LoadFailed
        message={products.error.message}
        onRetry={() => {
          void products.refetch();
        }}
      />
    );
  }

  const titleById = new Map(
    products.data.flatMap((product) =>
      product ? [[product.id, product.title] as const] : [],
    ),
  );

  const rows: MissingHsnRow[] = variants.data.items.map((variant) => ({
    ...variant,
    productTitle: titleById.get(variant.productId) ?? "",
  }));

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="text-muted-foreground bg-muted/30 flex items-start gap-2 border-b px-4 py-3 text-sm">
        <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
        <p>
          These variants have no HSN code and cannot be invoiced until one is
          set. Set it from the variant on its product page.
        </p>
      </div>

      <DataTable
        fields={fields}
        rows={rows}
        getRowId={(row) => row.id}
        project={projectMissingHsnVariant}
        sort={sort}
        onToggleSort={onToggleSort}
        emptyState={
          <NoMatches
            onClear={onClearFilter}
            description="No variants match the current filter."
          />
        }
        footer={
          <Pagination
            page={variants.data}
            limit={limit}
            onLimitChange={(next) => {
              setLimit(next);
              setHistory([undefined]);
            }}
            history={history}
            onNavigate={setHistory}
          />
        }
      />
    </div>
  );
}
